import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { CircularProgress, Step, StepLabel, Stepper, Theme, useMediaQuery } from '@mui/material';

import { CoreBox, CoreDialogContent, CoreTypography } from '@youscience/core';

import useCommonAppStore from '@store/CommonAppStore';
import useNotificationStore from '@store/NotificationStore';

import { ROUTES } from '@constants/ROUTES';

import CommonAppLogo from '@images/common-app-logo-button.svg?react';

import { useAnyCommonAppEnabled } from '@hooks/useAnyCommonAppEnabled';

import { DialogFooter } from '@components/DialogFooter/DialogFooter';
import ActionButton from '@components/ActionButton';

import { StyledAccountConnectionContent, sxStyles } from './AccountConnection.styles';
import { StyledDialog, StyledDialogTitle, sxStyles as dialogStyles } from './CommonAppDialog.styles';

export const AccountConnection = () => {
  const isMobileScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const { anyTenantHasCommonApp, isLearner } = useAnyCommonAppEnabled();

  const {
    applicant,
    createCommonAppLogin,
    getFerpaLink,
    getCommonAppLogin,
    isFerpaSigned,
    commonAppConnected,
    commonAppLogin,
    isLoading,
  } = useCommonAppStore((state) => state);

  const { notify } = useNotificationStore();

  const accountIsLinked = !!applicant?.applicantId;
  const collegeSelected = (applicant?.members?.length ?? 0) > 0;

  const [activeStep, setActiveStep] = useState(0);
  const [isBusy, setIsBusy] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleLinkAccount = async () => {
    if (isBusy) return;
    setIsBusy(true);

    const link = await createCommonAppLogin();

    const ceebCode = link.split('CeebCode=')[1];

    if (!ceebCode) {
      setIsBusy(false);

      notify({
        title: 'Unable to link account, school code is not valid.',
        message: '',
        severity: 'error',
      });

      return;
    }

    window.open(link, '_self');
  };

  const handleSignFerpaAgreement = async () => {
    if (isBusy) return;
    setIsBusy(true);

    const url = await getFerpaLink();

    window.open(url, '_self');
  };

  const handleAppLogin = async () => {
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleContinue = () => {
    if (commonAppLogin) {
      window.open(commonAppLogin, '_blank');
    }
    handleCloseDialog();
  };

  useEffect(() => {
    if (!anyTenantHasCommonApp) {
      return;
    }

    if (!commonAppLogin) {
      getCommonAppLogin();
    }

    if (!accountIsLinked) {
      setActiveStep(0);
    } else if (!isFerpaSigned()) {
      setActiveStep(1);
    } else if (!collegeSelected) {
      setActiveStep(2);
    }
  }, [applicant]);

  return (
    <CoreBox>
      <StyledAccountConnectionContent>
        {isLoading && <CircularProgress sx={{ position: 'absolute', left: '50%', transform: 'translate(0%, -50%)' }} />}
        {!commonAppConnected && !isLoading && anyTenantHasCommonApp ? (
          <>
            <CoreBox>
              <Stepper activeStep={activeStep} orientation={isMobileScreen ? 'vertical' : 'horizontal'}>
                <Step>
                  <StepLabel>
                    <CoreBox sx={sxStyles.labelContainer}>
                      <CoreTypography>Link your Common App account</CoreTypography>
                      <CoreTypography>Click the button below to begin.</CoreTypography>
                    </CoreBox>
                  </StepLabel>
                </Step>
                <Step>
                  <StepLabel>
                    <CoreBox>
                      <CoreTypography>Sign the FERPA agreement</CoreTypography>
                      <CoreTypography>Click the button below to begin.</CoreTypography>
                    </CoreBox>
                  </StepLabel>
                </Step>
                <Step>
                  <StepLabel>
                    <CoreBox>
                      <CoreTypography>Add Common App member schools</CoreTypography>

                      <CoreTypography>
                        <Link reloadDocument to={ROUTES.EDUCATION_PLANNER_APPLICATIONS}>
                          Refresh this page
                        </Link>{' '}
                        to view additions.
                      </CoreTypography>
                    </CoreBox>
                  </StepLabel>
                </Step>
              </Stepper>
            </CoreBox>
            <CoreBox sx={sxStyles.accountConnectionContent}>
              <CoreBox sx={sxStyles.accountConnectionContentItem}>
                {activeStep === 0 ? (
                  <ActionButton
                    onClick={handleLinkAccount}
                    startIcon={<CommonAppLogo />}
                    busyText="Loading"
                    disabled={!isLearner}
                    data-testid="link-your-common-app-account"
                  >
                    Link your Common App account
                  </ActionButton>
                ) : null}
                {activeStep === 1 ? (
                  <ActionButton
                    onClick={handleSignFerpaAgreement}
                    startIcon={<CommonAppLogo />}
                    busyText="Loading"
                    disabled={!isLearner}
                  >
                    FERPA release authorization
                  </ActionButton>
                ) : null}
                {activeStep === 2 ? (
                  <ActionButton
                    onClick={handleAppLogin}
                    startIcon={<CommonAppLogo />}
                    busyText="Loading"
                    disabled={!isLearner}
                  >
                    Go select colleges
                  </ActionButton>
                ) : null}
              </CoreBox>
            </CoreBox>
          </>
        ) : commonAppConnected ? (
          <CoreBox>
            <ActionButton
              data-testid="common-app-account-connected"
              onClick={handleAppLogin}
              startIcon={<CommonAppLogo />}
              disabled={!isLearner}
            >
              Access Your Common App account
            </ActionButton>
          </CoreBox>
        ) : null}
      </StyledAccountConnectionContent>

      <StyledDialog open={isDialogOpen} onClose={handleCloseDialog}>
        <StyledDialogTitle>Common App will open in a new tab</StyledDialogTitle>

        <CoreDialogContent sx={dialogStyles.contentBody}>
          Once you've finished adding colleges and completing your application requirements in Common App, please return
          to YouScience Brightpath to complete additional important steps—like adding your counselor and teachers,
          managing your to-do checklist, and tracking your application progress.
        </CoreDialogContent>

        <DialogFooter goNextButtonTitle="Continue" onGoNext={handleContinue} />
      </StyledDialog>
    </CoreBox>
  );
};
